/**
 * Overlay layer core (T-27/T-28, PRD §4.6 – §4.7, §3.9 – §3.10).
 *
 * Pure decisions (unit-tested in src/utils/__tests__/overlayCore.test.ts) plus
 * the few DOM helpers every overlay shares: the overlay stack (who owns
 * Escape), the focus trap, the scroll lock, `inert` on the app shell, the
 * portal root and anchored placement. No React here.
 */

// ─── Overlay stack: only the topmost overlay reacts to Escape ────────────────

const stack: string[] = [];

export function pushOverlay(id: string): void {
  removeOverlay(id);
  stack.push(id);
}

export function removeOverlay(id: string): void {
  const i = stack.indexOf(id);
  if (i >= 0) stack.splice(i, 1);
}

export function isTopOverlay(id: string): boolean {
  return stack.length > 0 && stack[stack.length - 1] === id;
}

export function overlayDepth(): number {
  return stack.length;
}

/** What Escape does for one overlay. A child that handled Escape first calls preventDefault. */
export function escapeDecision(opts: { isTop: boolean; dismissible: boolean; defaultPrevented: boolean }): 'close' | 'ignore' {
  if (!opts.isTop || !opts.dismissible || opts.defaultPrevented) return 'ignore';
  return 'close';
}

// ─── Focus trap ──────────────────────────────────────────────────────────────

export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'summary',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Where Tab should go inside a trapped container.
 * @param count   number of tabbable elements in the container
 * @param current index of the focused element among them (-1 = focus is outside / on the container)
 * @param shift   Shift+Tab
 * @returns the index to focus, -1 to focus the container itself (nothing tabbable),
 *          or null to let the browser move focus normally.
 */
export function trapTabTarget(count: number, current: number, shift: boolean): number | null {
  if (count <= 0) return -1;
  if (current < 0) return shift ? count - 1 : 0;
  if (shift && current === 0) return count - 1;
  if (!shift && current === count - 1) return 0;
  return null;
}

export function getTabbable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((el) => {
    if (el.tabIndex < 0) return false;
    if (el.closest('[inert]')) return false;
    // Visible only (display:none / hidden ancestors have no client rects).
    return el.getClientRects().length > 0;
  });
}

/** Handle a Tab keydown for a trapped container. Returns true when it moved focus. */
export function handleTrapTab(container: HTMLElement, e: KeyboardEvent): boolean {
  if (e.key !== 'Tab') return false;
  const items = getTabbable(container);
  const current = items.indexOf(document.activeElement as HTMLElement);
  const target = trapTabTarget(items.length, current, e.shiftKey);
  if (target === null) return false;
  e.preventDefault();
  (target === -1 ? container : items[target]).focus({ preventScroll: true });
  return true;
}

// ─── Variant resolution ──────────────────────────────────────────────────────

export type DialogVariant = 'center' | 'sheet' | 'drawer';

/** PRD §4.6: `center` becomes `sheet` below md, unless the caller opts out. */
export function resolveVariant(variant: DialogVariant, isDesktop: boolean, sheetOnMobile = true): DialogVariant {
  if (variant === 'center' && !isDesktop && sheetOnMobile) return 'sheet';
  return variant;
}

/**
 * Initial focus policy (PRD §4.6): on a sheet never jump into an input (the
 * phone keyboard would cover the sheet), focus the panel so the title is read.
 * On a desktop dialog, the first field (what Modal always did), else the panel.
 */
export function initialFocusTarget(variant: DialogVariant, hasExplicit: boolean): 'explicit' | 'first-field' | 'panel' {
  if (hasExplicit) return 'explicit';
  return variant === 'sheet' ? 'panel' : 'first-field';
}

// ─── Scroll lock (counted, so stacked overlays release once) ─────────────────

let lockCount = 0;
let saved: { overflow: string; paddingInlineEnd: string } | null = null;

export function lockScroll(): () => void {
  if (typeof document === 'undefined') return () => {};
  lockCount += 1;
  if (lockCount === 1) {
    const root = document.documentElement;
    const scrollbar = window.innerWidth - root.clientWidth;
    saved = { overflow: root.style.overflow, paddingInlineEnd: document.body.style.paddingInlineEnd };
    root.style.overflow = 'hidden';
    if (scrollbar > 0) document.body.style.paddingInlineEnd = `${scrollbar}px`;
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0 && saved) {
      document.documentElement.style.overflow = saved.overflow;
      document.body.style.paddingInlineEnd = saved.paddingInlineEnd;
      saved = null;
    }
  };
}

// ─── inert on the app shell while a modal dialog is open ─────────────────────
//
// The shell marks its regions with `data-overlay-inert` (sidebar, content
// column, tab bar). Toasts, the live regions and the floating timer stay
// outside, so announcements and "ביטול" keep working under a dialog.
// Safety valve: pages still have hand-made overlays (migrated in M5). If one of them is open inside a scope, that scope is not
// made inert, so a confirm opened from a page overlay is never frozen.

const INERT_SCOPE = '[data-overlay-inert]';
// Hand-made page overlays use the `.modal-content` panel class (and most also a dialog role).
const FOREIGN_OVERLAY = '.modal-content, [role="dialog"], [role="alertdialog"], [aria-modal="true"]';
let inertCount = 0;
let observer: MutationObserver | null = null;
let raf = 0;

function applyInert() {
  raf = 0;
  document.querySelectorAll<HTMLElement>(INERT_SCOPE).forEach((scope) => {
    const freeze = inertCount > 0 && !scope.querySelector(FOREIGN_OVERLAY);
    if (freeze) scope.setAttribute('inert', '');
    else scope.removeAttribute('inert');
  });
}

function scheduleInert() {
  if (!raf) raf = requestAnimationFrame(applyInert);
}

export function acquireInert(): () => void {
  if (typeof document === 'undefined') return () => {};
  inertCount += 1;
  applyInert();
  if (!observer && typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(scheduleInert);
    document.querySelectorAll(INERT_SCOPE).forEach((scope) => observer!.observe(scope, { childList: true, subtree: true }));
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    inertCount = Math.max(0, inertCount - 1);
    if (inertCount === 0) {
      observer?.disconnect();
      observer = null;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    }
    applyInert();
  };
}

// ─── Portal root ─────────────────────────────────────────────────────────────

export function getOverlayRoot(): HTMLElement {
  let el = document.getElementById('overlay-root');
  if (!el) {
    el = document.createElement('div');
    el.id = 'overlay-root';
    document.body.appendChild(el);
  }
  return el;
}

// ─── Anchored placement (popovers, menus) ────────────────────────────────────

export interface Rect { top: number; left: number; right: number; bottom: number; width: number; height: number }
export interface Placement { top: number; left: number; side: 'bottom' | 'top'; originX: 'left' | 'right'; maxHeight: number }

/**
 * Anchor for a popover that spans a wider element (a phone dropdown that takes
 * the whole card width under one tile): the vertical edges come from the
 * trigger, the horizontal edges from `span`.
 */
export function spanRect(anchor: Rect, span: Rect): Rect {
  return { top: anchor.top, bottom: anchor.bottom, height: anchor.height, left: span.left, right: span.right, width: span.width };
}

/**
 * How far to scroll the page down before a dropdown opens, so it fits UNDER
 * its trigger (a dropdown, not a popup that flips over the content): the room
 * it needs below the trigger, never pushing the trigger under the top bar.
 * `padTop` / `padBottom` are the sticky chrome (the html scroll padding).
 */
export function revealDelta(
  anchor: { top: number; bottom: number },
  viewportHeight: number,
  needed: number,
  padTop = 0,
  padBottom = 0,
  gap = 16,
): number {
  const room = viewportHeight - padBottom - anchor.bottom - gap;
  if (room >= needed) return 0;
  const maxUp = anchor.top - padTop;
  return Math.max(0, Math.round(Math.min(needed - room, maxUp)));
}

/**
 * Place a popover under (or above, when it does not fit) its anchor.
 * `align: 'end'` lines up the popover's inline-end edge with the anchor's
 * inline-end edge, so it reads correctly in RTL and LTR. The result is
 * clamped inside the viewport with `margin` on every side.
 */
export function placePopover(
  anchor: Rect,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  opts: { align: 'start' | 'end'; dir: 'rtl' | 'ltr'; gap?: number; margin?: number },
): Placement {
  const gap = opts.gap ?? 8;
  const margin = opts.margin ?? 8;
  // Physical edge the popover lines up with.
  const alignRight = (opts.align === 'end') === (opts.dir === 'ltr');
  let left = alignRight ? anchor.right - size.width : anchor.left;
  left = Math.min(Math.max(left, margin), Math.max(margin, viewport.width - margin - size.width));

  const below = viewport.height - anchor.bottom - gap - margin;
  const above = anchor.top - gap - margin;
  const side: 'bottom' | 'top' = size.height <= below || below >= above ? 'bottom' : 'top';
  const maxHeight = Math.max(120, side === 'bottom' ? below : above);
  const height = Math.min(size.height, maxHeight);
  const top = side === 'bottom' ? anchor.bottom + gap : anchor.top - gap - height;
  return { top, left, side, originX: alignRight ? 'right' : 'left', maxHeight };
}

// ─── Menu typeahead ──────────────────────────────────────────────────────────

/** Next item whose label starts with `key`, searching after `from` and wrapping. -1 when none. */
export function typeaheadIndex(labels: string[], from: number, key: string, disabled: boolean[] = []): number {
  const k = key.toLocaleLowerCase();
  const n = labels.length;
  for (let step = 1; step <= n; step += 1) {
    const i = (from + step + n) % n;
    if (!disabled[i] && labels[i].trim().toLocaleLowerCase().startsWith(k)) return i;
  }
  return -1;
}

/** Next enabled index for arrow / Home / End keys in a menu or listbox. */
export function moveIndex(
  key: 'next' | 'prev' | 'first' | 'last',
  current: number,
  disabled: boolean[],
  wrap = true,
): number {
  const n = disabled.length;
  const enabled = disabled.map((d, i) => (d ? -1 : i)).filter((i) => i >= 0);
  if (enabled.length === 0) return -1;
  if (key === 'first') return enabled[0];
  if (key === 'last') return enabled[enabled.length - 1];
  const step = key === 'next' ? 1 : -1;
  for (let k = 1; k <= n; k += 1) {
    const raw = current + step * k;
    if (!wrap && (raw < 0 || raw >= n)) return current;
    const i = ((raw % n) + n) % n;
    if (!disabled[i]) return i;
  }
  return current;
}
